import React from 'react';
import {
  convertFromRaw,
  convertToRaw,
  Editor,
  EditorState,
  RawDraftContentState,
} from 'draft-js';
import { useDebouncedCallback } from 'use-debounce';
import { WIDGET_DEBOUNCE_WHILE_TYPING } from '../../constants/app';
import type { Cell, ContentTableWidget } from '../../types';
import { TableCellEditor } from '../shared/styled-components/TableWidget';
import TableWidgetSkeleton from './TableWidgetSkeleton';

interface CellProps extends Cell {
  rowIndex: number;
  cellIndex: number;
  readOnly: boolean;
  onEditCellContent: (
    rowIndex: number,
    columnIndex: number,
    content: RawDraftContentState,
  ) => void;
}

const Cell: React.FC<CellProps> = ({
  rowIndex,
  cellIndex,
  content,
  readOnly,
  onEditCellContent,
}) => {
  const [editorState, setEditorState] = React.useState(
    EditorState.createEmpty(),
  );

  React.useEffect(() => {
    if (content) {
      setEditorState(EditorState.createWithContent(convertFromRaw(content)));
    }
  }, [content]);

  const updateCell = useDebouncedCallback(
    React.useCallback(
      (state: EditorState) =>
        onEditCellContent(
          rowIndex,
          cellIndex,
          convertToRaw(state.getCurrentContent()),
        ),
      [],
    ),
    WIDGET_DEBOUNCE_WHILE_TYPING,
  );

  const onChange = (state: EditorState) => {
    setEditorState(state);

    const newContent = state.getCurrentContent();
    const currentContent = editorState.getCurrentContent();

    if (currentContent !== newContent) {
      updateCell(state);
    }
  };

  const onBlur = () => {
    onEditCellContent(
      rowIndex,
      cellIndex,
      convertToRaw(editorState.getCurrentContent()),
    );
  };

  return (
    <TableCellEditor>
      <Editor
        editorState={editorState}
        onChange={onChange}
        readOnly={readOnly}
        onBlur={onBlur}
      />
    </TableCellEditor>
  );
};

interface Props {
  table: ContentTableWidget;
  readOnly: boolean;
  handleAddRow: (index: number) => void;
  handleAddColumn: (index: number) => void;
  handleRemoveRow: (index: number) => void;
  handleRemoveColumn: (index: number) => void;
  handleUpdateCellContent: (
    rowIndex: number,
    columnIndex: number,
    content: RawDraftContentState,
  ) => void;
}

const TableWidget: React.FC<Props> = ({
  table,
  readOnly,
  handleAddRow,
  handleAddColumn,
  handleRemoveRow,
  handleRemoveColumn,
  handleUpdateCellContent,
}) => {
  const onAddRow = (index: number) => {
    handleAddRow(index);
  };

  const onAddColumn = (index: number) => {
    handleAddColumn(index);
  };

  const onRemoveColumn = (index: number) => {
    handleRemoveColumn(index);
  };

  const onRemoveRow = (index: number) => {
    handleRemoveRow(index);
  };

  const onEditCellContent = (
    rowIndex: number,
    columnIndex: number,
    content: RawDraftContentState,
  ) => {
    handleUpdateCellContent(rowIndex, columnIndex, content);
  };

  return (
    <TableWidgetSkeleton
      rows={table.rows}
      isEditable={!readOnly}
      onAddRow={onAddRow}
      onAddColumn={onAddColumn}
      onRemoveColumn={onRemoveColumn}
      onRemoveRow={onRemoveRow}
    >
      {(rowIndex, cellIndex, content) => (
        <Cell
          rowIndex={rowIndex}
          cellIndex={cellIndex}
          content={content}
          readOnly={readOnly}
          onEditCellContent={onEditCellContent}
        />
      )}
    </TableWidgetSkeleton>
  );
};

export default TableWidget;
